import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Repeat,
  Plus,
  Play,
  Pause,
  Trash2,
  Zap,
  CalendarClock,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import {
  getRecurringInvoices,
  createRecurringInvoice,
  updateRecurringInvoice,
  deleteRecurringInvoice,
  runRecurringInvoiceNow,
} from '@/lib/api';

const FREQ_LABEL = { monthly: 'Monthly', weekly: 'Weekly' };
const MODE_LABEL = {
  previous_month: 'Previous full month',
  previous_week: 'Previous full week (Mon–Sun)',
  last_n_days: 'Last N days',
};

const emptyForm = {
  property_id: '',
  client_name: '',
  client_email: '',
  tax_rate: 0,
  frequency: 'monthly',
  billing_period_mode: 'previous_month',
  period_days: 30,
  day_of_month: 1,
  day_of_week: 1,
  custom_message: '',
  payment_instructions: '',
  notes: '',
  include_notes_in_pdf: true,
  due_days: 14,
  active: true,
};

export const RecurringInvoicesSection = ({ properties, onInvoiceGenerated }) => {
  const [rules, setRules] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const fetchRules = useCallback(async () => {
    try {
      const { data } = await getRecurringInvoices();
      setRules(data);
    } catch (err) {
      console.warn('Failed to fetch recurring invoice rules', err);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const openCreate = () => {
    setForm({ ...emptyForm, property_id: properties?.[0]?.id || '' });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.property_id) {
      toast.error('Select a property');
      return;
    }
    setBusy(true);
    try {
      await createRecurringInvoice(form);
      toast.success('Recurring rule created');
      setDialogOpen(false);
      await fetchRules();
    } catch (_) {
      toast.error('Failed to create rule');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (rule) => {
    try {
      await updateRecurringInvoice(rule.id, { active: !rule.active });
      await fetchRules();
    } catch (_) {
      toast.error('Failed to update');
    }
  };

  const runNow = async (rule) => {
    try {
      const { data } = await runRecurringInvoiceNow(rule.id);
      toast.success(`Draft invoice ${data.invoice_number} generated`);
      await fetchRules();
      onInvoiceGenerated?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to run rule';
      toast.error(msg);
    }
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete recurring rule for ${rule.property_name || 'this property'}?`)) return;
    try {
      await deleteRecurringInvoice(rule.id);
      await fetchRules();
      toast.success('Rule deleted');
    } catch (_) {
      toast.error('Failed to delete');
    }
  };

  const scheduleLabel = (r) => {
    if (r.frequency === 'monthly') {
      return `Runs on day ${r.day_of_month} of each month · covers ${MODE_LABEL[r.billing_period_mode]}${r.billing_period_mode === 'last_n_days' ? ` (${r.period_days}d)` : ''}`;
    }
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return `Runs every ${dayNames[r.day_of_week] || 'Mon'} · covers ${MODE_LABEL[r.billing_period_mode]}${r.billing_period_mode === 'last_n_days' ? ` (${r.period_days}d)` : ''}`;
  };

  return (
    <Card
      className="bg-white border border-slate-100"
      data-testid="recurring-invoices-section"
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-slate-100 rounded-lg">
              <Repeat className="h-4 w-4 text-slate-700" />
            </div>
            <div>
              <h3 className="font-manrope font-semibold text-slate-900">
                Recurring Invoices
              </h3>
              <p className="text-xs text-slate-500">
                Auto-generate draft invoices from tracked hours
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={openCreate}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-recurring-rule-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New Rule
          </Button>
        </div>

        {rules.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-6 border border-dashed border-slate-200 rounded">
            No recurring rules yet — add one to bill clients on autopilot.
          </div>
        ) : (
          <div className="space-y-2">
            {rules.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-lg border border-slate-100 hover:border-slate-200 px-3 py-2.5"
                data-testid={`recurring-rule-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-900 truncate">
                      {r.property_name || 'Untitled property'}
                    </p>
                    <span
                      className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${
                        r.active
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {r.active ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {r.client_name && (
                      <span className="mr-2">{r.client_name} ·</span>
                    )}
                    {FREQ_LABEL[r.frequency]} · {scheduleLabel(r)}
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                    <CalendarClock className="h-3 w-3" />
                    Next run: {r.next_run_date ? format(parseISO(r.next_run_date), 'MMM d, yyyy') : '—'}
                    {r.last_run_date && (
                      <span className="ml-2 text-slate-400">
                        (last: {format(parseISO(r.last_run_date), 'MMM d')})
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => runNow(r)}
                    title="Run now"
                    data-testid={`run-now-${r.id}`}
                  >
                    <Zap className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleActive(r)}
                    title={r.active ? 'Pause' : 'Resume'}
                    data-testid={`toggle-rule-${r.id}`}
                  >
                    {r.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(r)}
                    className="text-red-600 hover:bg-red-50"
                    data-testid={`delete-rule-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      {/* Create Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-[520px] max-h-[85vh] overflow-y-auto"
          data-testid="recurring-rule-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-manrope">Add Recurring Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Property *</Label>
              <Select
                value={form.property_id}
                onValueChange={(v) => setForm((p) => ({ ...p, property_id: v }))}
              >
                <SelectTrigger data-testid="rule-property-select">
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Client Name</Label>
                <Input
                  value={form.client_name}
                  onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))}
                  placeholder="Property Owner"
                />
              </div>
              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={form.client_email}
                  onChange={(e) => setForm((p) => ({ ...p, client_email: e.target.value }))}
                  placeholder="owner@example.com"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Frequency</Label>
                <Select
                  value={form.frequency}
                  onValueChange={(v) => setForm((p) => ({ ...p, frequency: v }))}
                >
                  <SelectTrigger data-testid="rule-frequency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.frequency === 'monthly' ? (
                <div className="space-y-2">
                  <Label>Day of Month (1-28)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="28"
                    value={form.day_of_month}
                    onChange={(e) => setForm((p) => ({ ...p, day_of_month: parseInt(e.target.value) || 1 }))}
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Day of Week</Label>
                  <Select
                    value={String(form.day_of_week)}
                    onValueChange={(v) => setForm((p) => ({ ...p, day_of_week: parseInt(v) }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => (
                        <SelectItem key={d} value={String(i)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Billing Period</Label>
              <Select
                value={form.billing_period_mode}
                onValueChange={(v) => setForm((p) => ({ ...p, billing_period_mode: v }))}
              >
                <SelectTrigger data-testid="rule-billing-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="previous_month">Previous full month</SelectItem>
                  <SelectItem value="previous_week">Previous full week (Mon–Sun)</SelectItem>
                  <SelectItem value="last_n_days">Last N days</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                The window of completed tasks each auto-invoice will cover.
              </p>
            </div>

            {form.billing_period_mode === 'last_n_days' && (
              <div className="space-y-2">
                <Label>Number of Days</Label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={form.period_days}
                  onChange={(e) => setForm((p) => ({ ...p, period_days: parseInt(e.target.value) || 30 }))}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={form.tax_rate}
                  onChange={(e) => setForm((p) => ({ ...p, tax_rate: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Due (days)</Label>
                <Input
                  type="number"
                  min="0"
                  max="90"
                  value={form.due_days}
                  onChange={(e) => setForm((p) => ({ ...p, due_days: parseInt(e.target.value) || 14 }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Message on PDF (optional)</Label>
              <Textarea
                rows={2}
                value={form.custom_message}
                onChange={(e) => setForm((p) => ({ ...p, custom_message: e.target.value }))}
                placeholder="Thank you for your business!"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Instructions (optional)</Label>
              <Textarea
                rows={2}
                value={form.payment_instructions}
                onChange={(e) => setForm((p) => ({ ...p, payment_instructions: e.target.value }))}
                placeholder="Wire to Bank of X, Acct #12345..."
              />
            </div>

            <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
              <div className="text-sm text-slate-600">Active immediately</div>
              <Switch
                checked={form.active}
                onCheckedChange={(v) => setForm((p) => ({ ...p, active: v }))}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={busy}
                className="bg-slate-900 hover:bg-slate-800"
                data-testid="submit-recurring-rule-btn"
              >
                Create Rule
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RecurringInvoicesSection;
